import React from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import { partnerSectionLinks } from "../../constants";
import { FeatureFlags, useFeatureFlag } from "../../featureFlags";
import { useStrings } from "../../localization/localizationHelpers";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import { XboxOnboardApplicationStatus } from "../../server/crm/api/models/xboxOnboardModels";
import * as styles from "./PartnerSectionNav.module.css";

export const PartnerSectionNav = () => {
  const location = useLocation();
  const data = useSelector(getXboxOnboardData);
  const isDeviceOrderingEnabled = useFeatureFlag(FeatureFlags.EnableDeviceOrderingAddresses);
  const isSupportEnabled = useFeatureFlag(FeatureFlags.EnableHubSupportCases);
  const isHubAdmin = data?.isHubAdmin === true;
  const hasDevelopmentHardwareAccess = data?.hasDevelopmentHardwareAccess === true;
  const isAaaUser = data?.hubType === "aaa";
  const isAaaHubAdmin = isAaaUser && isHubAdmin;
  const isOnboardingHubProfileMode = data?.isOnboardingHubProfileMode === true;
  const isNdaSigned = data?.status === XboxOnboardApplicationStatus.NdaSigned;
  const canManagePartner = isHubAdmin || isOnboardingHubProfileMode;
  const sectionLinks = [
    partnerSectionLinks.overview,
    partnerSectionLinks.admin,
    partnerSectionLinks.members,
    partnerSectionLinks.agreements,
    partnerSectionLinks.settings,
    partnerSectionLinks.developmentHardware,
    partnerSectionLinks.deviceOrderingAddresses,
    partnerSectionLinks.invitations,
    partnerSectionLinks.support,
  ];
  const links = sectionLinks.filter(link => {
    if (link === partnerSectionLinks.overview) {
      return (isNdaSigned || isAaaUser) && !isOnboardingHubProfileMode;
    }
    if (link === partnerSectionLinks.developmentHardware) {
      return isNdaSigned && isHubAdmin;
    }
    if (link === partnerSectionLinks.deviceOrderingAddresses) {
      return isDeviceOrderingEnabled && isNdaSigned && isHubAdmin && hasDevelopmentHardwareAccess;
    }
    if (link === partnerSectionLinks.settings) {
      return true;
    }
    if (link === partnerSectionLinks.support) {
      return isSupportEnabled;
    }
    if (link === partnerSectionLinks.agreements) {
      return !isAaaUser && (isHubAdmin || !isNdaSigned);
    }
    if (
      link === partnerSectionLinks.admin ||
      link === partnerSectionLinks.members ||
      link === partnerSectionLinks.invitations
    ) {
      return (
        link === partnerSectionLinks.invitations ||
        (link === partnerSectionLinks.admin &&
          (isAaaHubAdmin || (isNdaSigned && canManagePartner))) ||
        (link === partnerSectionLinks.members &&
          !isOnboardingHubProfileMode &&
          isNdaSigned &&
          !isHubAdmin)
      );
    }

    return true;
  });
  const localizedStrings = useStrings(
    ...links.map(link => link.labelKey),
    "partnerSectionNavigationLabel"
  );
  const labels = localizedStrings.slice(0, links.length);
  const navigationLabel = localizedStrings[links.length];
  const activeLink = links.find(link => location.pathname.startsWith(link.path));

  if (!data || !activeLink) {
    return null;
  }

  return (
    <nav className={styles.nav} aria-label={navigationLabel}>
      <ul className={styles.list}>
        {links.map((link, index) => {
          const isActive = link === activeLink;
          return (
            <li key={link.path}>
              <Link
                className={isActive ? `${styles.link} ${styles.active}` : styles.link}
                to={link.to}
                aria-current={isActive ? "page" : undefined}
              >
                {labels[index]}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
